import { v4 as uuidv4 } from "uuid";
import type { PlainWorkflowAction, PlainWorkflowStep, PlainWorkflowTrigger } from "@/queries/automation-queries";
import { type PlainMessage } from "@bufbuild/protobuf";
import {
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTrigger_PullRequest,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
    WorkflowTriggerContext_Repositories,
    type WorkflowStep,
    type WorkflowTrigger_PullRequestEvent,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";

export type WorkflowEditFormLimitsData = {
    maxParallel?: number;
    maxTotal?: number;
};

export type WorkflowEditFormData = {
    name: string;
    description: string;
    executor?: PlainMessage<Subject>;
    triggers: WorkflowEditFormTriggerData[];
    steps: WorkflowEditFormStepData[];
    limits?: WorkflowEditFormLimitsData;
};

export type WorkflowEditFormTriggerType = "manual" | "time" | "pullRequest";

/**
 * The type of contexts for a manually-triggered workflow.
 */
export enum WorkflowEditFormTriggerContextType {
    /**
     * Targets external SCM repositories the workflow should work with.
     */
    Repositories = "repositories",

    /**
     * Targets projects the workflow should work with.
     */
    Projects = "projects",
}

/**
 * A manual trigger context that specifies the projects the workflow should work with
 */
export type WorkflowEditFormTriggerProjectContext = {
    type: WorkflowEditFormTriggerContextType.Projects;
    projectIds: string[];
};

/**
 * A manual trigger context that specifies repositories the workflow should work with
 */
export type WorkflowEditFormTriggerRepositoryContext = {
    type: WorkflowEditFormTriggerContextType.Repositories;

    /**
     * An scm-specific search string that will be used to find repositories to work with
     */
    repoSearchString: string;

    /**
     * The SCM host where the search should be performed (e.g., "github.com", "gitlab.com")
     */
    scmHost: string;

    /**
     * The ID of the runner that will be used for the workflow.
     */
    runnerId: string;

    /**
     * The ID of the environment class that will be used for the workflow.
     */
    environmentClassId: string;
};

export type WorkflowEditFormTriggerContext =
    | WorkflowEditFormTriggerProjectContext
    | WorkflowEditFormTriggerRepositoryContext;

export type WorkflowEditFormTriggerConfigurationData = {
    time?: { cronExpression: string };
    pullRequest?: { events: WorkflowTrigger_PullRequestEvent[] };
};

export type WorkflowEditFormTriggerData = {
    id: string; // temporary ID for form management
    type: WorkflowEditFormTriggerType;
    context: WorkflowEditFormTriggerContext;
    config: WorkflowEditFormTriggerConfigurationData;
};

export type WorkflowEditFormStepType = "command" | "prompt";

export type WorkflowEditFormStepData = {
    id: string; // temporary ID for form management
    type: WorkflowEditFormStepType;
    content: string;
};

export const convertWorkflowActionToFormSteps = (action?: PlainWorkflowAction): WorkflowEditFormStepData[] => {
    if (!action?.steps || action.steps.length === 0) {
        return [];
    }

    return action.steps.map((step, index) => {
        const id = `step-${index}-${uuidv4()}`;
        if (step.step?.case === "task") {
            return {
                id,
                type: "command" as const,
                content: step.step.value.command || "",
            };
        } else if (step.step?.case === "agent") {
            return {
                id,
                type: "prompt" as const,
                content: step.step.value.prompt || "",
            };
        }

        // Fallback for unknown step types
        return {
            id,
            type: "command" as const,
            content: "",
        };
    });
};

export const convertWorkflowActionToFormLimits = (
    action?: PlainWorkflowAction,
): WorkflowEditFormLimitsData | undefined => {
    if (!action?.limits) {
        return undefined;
    }

    const limits: WorkflowEditFormLimitsData = {};

    if (action.limits.maxParallel && action.limits.maxParallel > 0) {
        limits.maxParallel = action.limits.maxParallel;
    }

    if (action.limits.maxTotal && action.limits.maxTotal > 0) {
        limits.maxTotal = action.limits.maxTotal;
    }

    return Object.keys(limits).length > 0 ? limits : undefined;
};

export const convertFormStepsToWorkflowStep = (steps: WorkflowEditFormStepData[]): PlainWorkflowStep[] => {
    const workflowSteps: PlainMessage<WorkflowStep>[] = steps.map((step) => {
        if (step.type === "command") {
            return {
                step: {
                    case: "task" as const,
                    value: {
                        command: step.content,
                    },
                },
            };
        } else {
            return {
                step: {
                    case: "agent" as const,
                    value: {
                        prompt: step.content,
                    },
                },
            };
        }
    });
    return workflowSteps;
};

export const convertTriggersToFormData = (triggers?: PlainWorkflowTrigger[]): WorkflowEditFormTriggerData[] => {
    if (!triggers) return [];

    return triggers
        .map((trigger): WorkflowEditFormTriggerData | null => {
            const id = `trigger-${uuidv4()}`;

            if (!trigger.trigger?.case) {
                console.warn("Trigger configuration ignored as the trigger type wasn't set.");
                return null;
            }

            const triggerCtx = trigger.context?.context;

            const cfg: WorkflowEditFormTriggerData = {
                id,
                config: {},
                context: {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                },
                type: trigger.trigger?.case,
            };

            switch (trigger.trigger?.case) {
                case "manual":
                    break;
                case "pullRequest":
                    if (trigger.trigger.value?.events !== undefined) {
                        cfg.config.pullRequest = {
                            events: trigger.trigger.value.events,
                        };
                    }
                    break;
                default:
                    console.warn("Trigger configuration ignored, unsupported trigger type.", trigger);
                    return null;
            }

            switch (triggerCtx?.case) {
                case "projects":
                    cfg.context = {
                        type: WorkflowEditFormTriggerContextType.Projects,
                        projectIds: triggerCtx.value.projectIds,
                    };
                    break;
                case "repositories": {
                    const { repositorySelector, environmentClassId } = triggerCtx.value;

                    let repoSearchString: string;
                    let scmHost: string;
                    if (repositorySelector.case === "repoSelector") {
                        repoSearchString = repositorySelector.value.repoSearchString;
                        scmHost = repositorySelector.value.scmHost;
                    } else {
                        repoSearchString = "";
                        scmHost = "";
                    }

                    cfg.context = {
                        type: WorkflowEditFormTriggerContextType.Repositories,
                        repoSearchString,
                        scmHost,
                        runnerId: "",
                        environmentClassId,
                    };
                    break;
                }
                default:
                    console.warn(
                        "Trigger context ignored, we only support projects or repositories context for now.",
                        trigger,
                    );
                    return null;
            }

            return cfg;
        })
        .filter((t): t is WorkflowEditFormTriggerData => t !== null);
};

export const convertFormTriggersToProtobuf = (triggers: WorkflowEditFormTriggerData[]): PlainWorkflowTrigger[] => {
    return triggers.map((trigger) => {
        let triggerCtx: WorkflowTriggerContext | undefined;
        switch (trigger.context.type) {
            case WorkflowEditFormTriggerContextType.Projects:
                triggerCtx = new WorkflowTriggerContext({
                    context: {
                        case: "projects",
                        value: new WorkflowTriggerContext_Projects({
                            projectIds: trigger.context.projectIds,
                        }),
                    },
                });
                break;
            case WorkflowEditFormTriggerContextType.Repositories:
                triggerCtx = new WorkflowTriggerContext({
                    context: {
                        case: "repositories",
                        value: new WorkflowTriggerContext_Repositories({
                            repositorySelector: {
                                case: "repoSelector",
                                value: {
                                    repoSearchString: trigger.context.repoSearchString,
                                    scmHost: trigger.context.scmHost,
                                },
                            },
                            environmentClassId: trigger.context.environmentClassId,
                        }),
                    },
                });
                break;
            default:
                break;
        }

        if (trigger.config.pullRequest) {
            return new WorkflowTrigger({
                trigger: {
                    case: "pullRequest",
                    value: new WorkflowTrigger_PullRequest({
                        events: trigger.config.pullRequest.events,
                    }),
                },
                context: triggerCtx,
            });
        }

        if (trigger.config.time) {
            console.warn("Time-based triggers are not yet supported in the protobuf conversion.");
        }

        return new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: triggerCtx,
        });
    });
};
